import React, { useState, useEffect, useMemo } from "react";

import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Dropdown,
  Spinner,
} from "react-bootstrap";

import { useTranslation } from "react-i18next";

import { Plus, Trash2, AlertCircle, Briefcase } from "lucide-react";

import { useSelector, useDispatch } from "react-redux";

import { toast } from "react-toastify";

import {
  closeWorkTimeRegisterModal,
  submitWorkTimeRegisters,
} from "../../../redux/slices/workTimeRegisterSlice";

const WorkTimeRegisterModal = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { isModalOpen, suggestions, loading, submitting } = useSelector(
    (state) => state.workTimeRegister,
  );

  const minutesToHM = (minutes) => {
    const h = Math.floor(minutes / 60);

    const m = minutes % 60;

    return `${h}h ${m}m`;
  };

  const [entries, setEntries] = useState([]);

  const [errorMessage, setErrorMessage] = useState("");

  const [totalAllocated, setTotalAllocated] = useState(0);

  const presenceHours = suggestions?.total_worked_hours_today || 0;

  const presenceMinutes = Math.round(presenceHours * 60);

  const updateEntryMinutes = (id, totalMinutes) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, totalMinutes } : entry,
      ),
    );
  };

  const safeProjects = useMemo(
    () => suggestions?.projects || [],

    [suggestions?.projects],
  );

  const safeActivityTypes = useMemo(
    () => suggestions?.activity_types || [],

    [suggestions?.activity_types],
  );

  useEffect(() => {
    if (!isModalOpen) {
      setEntries([]);

      setErrorMessage("");
    } else if (!loading && safeProjects.length > 0 && entries.length === 0) {
      const initialEntries = safeProjects.map((p) => ({
        id: p.id,

        name: p.name,

        type: "Project",

        totalMinutes: Math.round((p.suggested_hours || 0) * 60),

        suggested_hours: p.suggested_hours,

        color: p.color,
      }));

      setEntries(initialEntries);
    }
  }, [isModalOpen, loading, safeProjects, entries.length]);

  useEffect(() => {
    const total = entries.reduce(
      (sum, entry) => sum + (entry.totalMinutes || 0),

      0,
    );

    setTotalAllocated(total);
  }, [entries]);

  const addEntryFromDropdown = (item, isProject = true) => {
    if (entries.find((e) => e.id === item.id)) return;

    setEntries([
      ...entries,

      {
        id: item.id,

        name: item.name,

        type: isProject ? "Project" : item.type,

        totalMinutes: isProject
          ? Math.round((item.suggested_hours || 0) * 60)
          : 0,

        suggested_hours: isProject ? item.suggested_hours : null,

        color: isProject ? item.color : null,
      },
    ]);
  };

  const removeEntry = (id) => {
    setEntries(entries.filter((entry) => entry.id !== id));
  };

  const handleFinalSubmit = async () => {
    if (totalAllocated !== presenceMinutes) {
      setErrorMessage(
        t("components.WorkTimeRegisterModal.error_mismatch", {
          total: minutesToHM(totalAllocated),
          presence: minutesToHM(presenceMinutes),
        }),
      );
      return;
    }

    setErrorMessage("");

    const validEntries = entries.filter((entry) => entry.totalMinutes > 0);

    const formattedData = validEntries.map((entry) => ({
      project_id: entry.type === "Project" ? entry.id : null,
      type: entry.type,
      minutes: entry.totalMinutes,
      register_date:
        suggestions?.register_date || new Date().toISOString().split("T")[0],
      comments: t(
        "components.WorkTimeRegisterModal.manual_distribution_comment",
      ),
    }));

    try {
      await dispatch(submitWorkTimeRegisters(formattedData)).unwrap();
      toast.success(t("components.WorkTimeRegisterModal.success_toast"));
      dispatch(closeWorkTimeRegisterModal());
    } catch (err) {
      setErrorMessage(
        t("components.WorkTimeRegisterModal.error_saving") +
          " " +
          (err.detail ||
            t("components.WorkTimeRegisterModal.check_connection")),
      );
    }
  };

  if (!isModalOpen) return null;

  const availableProjects = safeProjects.filter(
    (p) => !entries.find((e) => e.id === p.id),
  );

  const availableGenerics = safeActivityTypes.filter(
    (type) => type !== "Project" && !entries.find((e) => e.id === type),
  );

  return (
    <Modal
      show={isModalOpen}
      onHide={() => dispatch(closeWorkTimeRegisterModal())}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      className="atlar-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title
          className="txt-main fw-bold"
          style={{ fontFamily: "var(--font-main)" }}
        >
          {loading
            ? t("components.WorkTimeRegisterModal.loading_title")
            : t("components.WorkTimeRegisterModal.title")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="custom-scrollbar" style={{ minHeight: "400px" }}>
        {loading ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5">
            <Spinner
              animation="border"
              style={{ color: "var(--brand-primary)" }}
              className="mb-3"
            />

            <p className="txt-subtle">
              {t("components.WorkTimeRegisterModal.loading_msg")}
            </p>
          </div>
        ) : (
          <>
            <div
              className="atlar-card p-3 mb-4 bg-surface"
              style={{ borderLeft: "5px solid var(--brand-primary)" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="txt-subtle mb-0 small text-uppercase">
                    {t(
                      "components.WorkTimeRegisterModal.presence_registered_today",
                    )}
                  </p>

                  <h3 className="txt-main mb-0">
                    {minutesToHM(presenceMinutes)}
                  </h3>
                </div>

                <div className="text-end">
                  <p className="txt-subtle mb-0 small text-uppercase">
                    {t("components.WorkTimeRegisterModal.total_allocated")}
                  </p>

                  <h3
                    className={`mb-0 ${totalAllocated === presenceMinutes ? "txt-success" : "txt-danger"}`}
                  >
                    {minutesToHM(totalAllocated)}
                  </h3>
                </div>
              </div>
            </div>

            {errorMessage && (
              <div className="theme-alert-danger d-flex align-items-center gap-2 mb-3">
                <AlertCircle size={18} />

                <span>{errorMessage}</span>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5
                className="txt-main mb-0"
                style={{ fontSize: "var(--text-small)", letterSpacing: "1px" }}
              >
                {t("components.WorkTimeRegisterModal.timesheet")}
              </h5>

              <Dropdown>
                <Dropdown.Toggle
                  variant="light"
                  size="sm"
                  className="d-flex align-items-center gap-2 border shadow-sm px-3 bg-white"
                >
                  <Plus size={16} />{" "}
                  {t("components.WorkTimeRegisterModal.add_row")}
                </Dropdown.Toggle>

                <Dropdown.Menu
                  className="shadow border-0"
                  style={{ minWidth: "250px" }}
                >
                  <Dropdown.Header className="txt-subtle">
                    {t("components.WorkTimeRegisterModal.my_projects")}
                  </Dropdown.Header>

                  {availableProjects.length > 0 ? (
                    availableProjects.map((p) => (
                      <Dropdown.Item
                        key={p.id}
                        onClick={() => addEntryFromDropdown(p, true)}
                      >
                        {p.name}
                      </Dropdown.Item>
                    ))
                  ) : (
                    <Dropdown.Item disabled>
                      {safeProjects.length === 0
                        ? t("components.WorkTimeRegisterModal.no_projects_db")
                        : t("components.WorkTimeRegisterModal.all_added")}
                    </Dropdown.Item>
                  )}

                  <Dropdown.Divider />

                  <Dropdown.Header className="txt-subtle">
                    {t("components.WorkTimeRegisterModal.other_activities")}
                  </Dropdown.Header>

                  {availableGenerics.length > 0 ? (
                    availableGenerics.map((type) => (
                      <Dropdown.Item
                        key={type}
                        onClick={() =>
                          addEntryFromDropdown(
                            { id: type, name: type, type: type },

                            false,
                          )
                        }
                      >
                        {type}
                      </Dropdown.Item>
                    ))
                  ) : (
                    <Dropdown.Item disabled>
                      {t("components.WorkTimeRegisterModal.types_not_loaded")}
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div className="border rounded bg-surface overflow-hidden shadow-sm">
              <Row className="bg-surface py-2 border-bottom m-0 px-2 txt-subtle small fw-bold">
                <Col xs={3}>
                  {t("components.WorkTimeRegisterModal.header_type")}
                </Col>

                <Col xs={5}>
                  {t("components.WorkTimeRegisterModal.header_activity")}
                </Col>

                <Col xs={3} className="text-center">
                  {t("components.WorkTimeRegisterModal.header_hours")}
                </Col>

                <Col xs={1}></Col>
              </Row>

              <div className="p-2">
                {entries.length === 0 ? (
                  <div className="text-center py-5">
                    <Briefcase
                      size={32}
                      className="txt-subtle mb-2"
                      opacity={0.3}
                    />

                    <p className="txt-subtle small mb-0">
                      {t("components.WorkTimeRegisterModal.empty_list_msg")}
                    </p>
                  </div>
                ) : (
                  entries.map((entry) => (
                    <Row
                      key={entry.id}
                      className="align-items-center py-2 m-0 border-bottom animate__animated animate__fadeIn"
                    >
                      <Col xs={3}>
                        <span
                          className="shadow-sm text-uppercase d-inline-flex align-items-center justify-content-center"
                          style={{
                            backgroundColor:
                              entry.type === "Project"
                                ? entry.color || "var(--brand-primary)"
                                : "var(--gray-500)",
                            color: "#ffffff",
                            padding: "0.35rem 0.8rem",
                            borderRadius: "12px",
                            fontSize: "0.7rem",
                            fontWeight: "600",
                            letterSpacing: "0.5px",
                            minWidth: "85px",
                          }}
                        >
                          {entry.type === "Project"
                            ? t("components.WorkTimeRegisterModal.project")
                            : entry.name}
                        </span>
                      </Col>

                      <Col
                        xs={5}
                        className="txt-main fw-semibold d-flex flex-column"
                      >
                        <span className="text-truncate">{entry.name}</span>

                        {entry.suggested_hours && (
                          <small
                            className="txt-subtle"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {t(
                              "components.WorkTimeRegisterModal.suggested_hours_label",
                            )}
                            :{" "}
                            {minutesToHM(
                              Math.round(entry.suggested_hours * 60),
                            )}
                          </small>
                        )}
                      </Col>

                      <Col xs={3}>
                        <div className="d-flex align-items-center gap-1">
                          <Form.Control
                            type="number"
                            min="0"
                            value={Math.floor(entry.totalMinutes / 60)}
                            onChange={(e) => {
                              const h = parseInt(e.target.value) || 0;

                              const m = entry.totalMinutes % 60;

                              updateEntryMinutes(
                                entry.id,

                                h * 60 + m,
                              );
                            }}
                            className="form-control-sm text-center"
                            style={{ width: "60px" }}
                          />

                          <span className="small text-muted">h</span>

                          <Form.Control
                            type="number"
                            min="0"
                            max="59"
                            value={entry.totalMinutes % 60}
                            onChange={(e) => {
                              const h = Math.floor(entry.totalMinutes / 60);

                              const m = Math.min(
                                59,

                                parseInt(e.target.value) || 0,
                              );

                              updateEntryMinutes(
                                entry.id,

                                h * 60 + m,
                              );
                            }}
                            className="form-control-sm text-center"
                            style={{ width: "60px" }}
                          />

                          <span className="small text-muted">m</span>
                        </div>
                      </Col>

                      <Col xs={1} className="text-end">
                        <Button
                          variant="link"
                          className="txt-danger p-0"
                          onClick={() => removeEntry(entry.id)}
                        >
                          <Trash2 size={18} />
                        </Button>
                      </Col>
                    </Row>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 mt-3">
        <Button
          variant="link"
          className="txt-subtle text-decoration-none fw-bold"
          onClick={() => dispatch(closeWorkTimeRegisterModal())}
        >
          {t("components.WorkTimeRegisterModal.cancel")}
        </Button>

        <Button
          disabled={submitting || loading || entries.length === 0}
          style={{
            backgroundColor: "var(--brand-primary)",

            borderColor: "var(--brand-primary)",

            padding: "10px 25px",

            borderRadius: "var(--btn-radius)",

            fontFamily: "var(--font-main)",

            fontSize: "var(--text-small)",
          }}
          onClick={handleFinalSubmit}
        >
          {submitting
            ? t("components.WorkTimeRegisterModal.saving")
            : t("components.WorkTimeRegisterModal.confirm_registers")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WorkTimeRegisterModal;
